// ═══════════════════════════════════════════════════════════════════════════
// Swachhata Hi Seva: Solid Waste Management Drive — learning material
//
// Three modules by class band (Classes 1–5, 6–8, 9–12), transcribed from the
// Ministry's "Solid Waste Management Rules: 2026 | Learning Material" PDF.
// The same deck is read by schools (post-login popup) and by students (public
// page); the quiz bank is separate (Backend2/data/swachhataQuizBank.js).
// ═══════════════════════════════════════════════════════════════════════════

import posterCompost from "../../../assets/images/swachhata/poster-compost.jpg";
import poster4Bin from "../../../assets/images/swachhata/poster-4bin.jpg";
import posterDosDonts from "../../../assets/images/swachhata/poster-dos-donts.jpg";
import posterCleanGreen from "../../../assets/images/swachhata/poster-clean-green.jpg";
import posterBwg from "../../../assets/images/swachhata/poster-bwg.jpg";
import posterWasteWarriors from "../../../assets/images/swachhata/poster-waste-warriors.jpg";

export const CAMPAIGN = {
  name: "Swachhata Hi Seva",
  tagline: "Solid Waste Management Drive",
  // Campaign slogan — shown next to the Solid Waste Management title everywhere.
  slogan: "Swachh Paathshala: Yuva for Swachhata",
  programme: "Eco Clubs for Mission LiFE",
  rules: "Solid Waste Management Rules, 2026",
  rulesShort: "SWM Rules, 2026",
  rulesEffective: "1 April 2026",
  ministry: "Ministry of Education · Department of School Education & Literacy",
};

// The four streams — Rule 3(1) definitions, bin colours from the Ministry poster.
export const BINS = [
  {
    key: "wet",
    name: "Wet Waste",
    colour: "Green Bin",
    hex: "#22a447",
    dark: "#157a33",
    clause: "Rule 3(1)(zzl)",
    definition: "Organic waste including kitchen waste, food waste, vegetable waste, meat waste, fruit waste, flower waste and such similar waste and biodegradable waste.",
    examples: "Kitchen waste, vegetables, fruit peels, leftover food, flowers, garden leaves",
  },
  {
    key: "dry",
    name: "Dry Waste",
    colour: "Blue Bin",
    hex: "#1e6fd9",
    dark: "#154f9c",
    clause: "Rule 3(1)(s)",
    definition: "Waste other than wet waste, sanitary waste and special care waste; includes recyclable waste and non-recyclable waste.",
    examples: "Notebook paper, cardboard, plastic, glass, rubber, wood, metal cans",
  },
  {
    key: "sanitary",
    name: "Sanitary Waste",
    colour: "Red Bin",
    hex: "#d93a3a",
    dark: "#a32626",
    clause: "Rule 3(1)(zp)",
    definition: "Waste comprising used diapers, sanitary towels or napkins, tampons, condoms, incontinence sheets and any other similar waste.",
    examples: "Used diapers and sanitary towels, securely wrapped",
  },
  {
    key: "special",
    name: "Special Care Waste",
    colour: "Black Bin",
    hex: "#2b2b2b",
    dark: "#111111",
    clause: "Rule 3(1)(zx)",
    definition: "Discarded paint drums, pesticide cans or containers or bottles, compact fluorescent lamps or bulbs, tube lights, expired medicines, broken mercury thermometers, waste batteries, used or waste needles and syringes and contaminated gauze, generated at the household level.",
    examples: "Paint cans, expired medicines, burnt-out bulbs, batteries",
  },
];

export const POSTERS = [
  { key: "4bin", title: "4-Bin Code Breakdown", src: poster4Bin },
  { key: "dosdonts", title: "Dos and Don'ts for Students", src: posterDosDonts },
  { key: "compost", title: "From Campus Waste to Green Manure", src: posterCompost },
  { key: "bwg", title: "Who are Bulk Waste Generators?", src: posterBwg },
  { key: "clean", title: "Clean & Green Society Starts with Schools", src: posterCleanGreen },
  { key: "warriors", title: "Waste Warriors", src: posterWasteWarriors },
];

// ─── Downloading the creatives ─────────────────────────────────────────────
// The posters are bundled with the app, so they are same-origin and a plain
// <a download> saves them. Open to everyone — no login, no API call.
export const posterFileName = (poster) =>
  `Swachhata-Hi-Seva-2026-${String(poster?.title || "poster").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "")}.jpg`;

export const downloadPoster = (poster) => {
  if (!poster?.src) return;
  const a = document.createElement("a");
  a.href = poster.src;
  a.download = posterFileName(poster);
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

// Saved one after another: browsers refuse a burst of downloads fired together.
export const downloadAllPosters = async (onProgress) => {
  for (let i = 0; i < POSTERS.length; i += 1) {
    downloadPoster(POSTERS[i]);
    if (onProgress) onProgress(i + 1, POSTERS.length);
    if (i < POSTERS.length - 1) await new Promise((r) => setTimeout(r, 600));
  }
};

// ─── Learning material ─────────────────────────────────────────────────────
// Source: "Swachhata Hi Seva — Solid Waste Management Rules: 2026 | Learning
// Material" (Department of School Education & Literacy, Ministry of Education;
// Solid_Waste_Management_Learning_Material_Classes_1_to_12.pdf, 3 modules by
// class band). The text below is that document, transcribed as written; tables
// are rendered as lists. Edit the document first, then mirror it here.
export const MATERIAL = {
  title: "Solid Waste Management Rules: 2026",
  motto: "Segregate at source, sustain the future today, let's lead the way to a cleaner, greener day!",
  keyMessage: "Key message for every age group: Start with less waste, separate what you create, ensure it reaches the right place.",
};

const MODULE_1 = {
  key: "m1",
  type: "note",
  rail: "Classes 1–5",
  title: "Module 1: Classes 1–5",
  blocks: [
    { h: "Our waste, our responsibility" },
    { p: "Every day we throw away fruit peels, food leftovers, paper, wrappers and many other things. Waste does not vanish when it enters a dustbin. It is collected, sorted, composted, recycled or safely disposed of. If waste is mixed, useful materials may be spoiled and pollution can increase." },
    { h: "The four bins" },
    { list: [
      { lead: "Green — wet waste.", text: "Fruit peels, leftover food, tea leaves, flowers and leaves." },
      { lead: "Blue — dry waste.", text: "Paper, cardboard, clean plastic, glass, metal, rubber and wood." },
      { lead: "Red — sanitary waste.", text: "Used diapers, sanitary towels, napkins and similar items; wrap safely." },
      { lead: "Black — special care waste.", text: "Batteries, expired medicines, CFLs, tube lights, paint or pesticide containers." },
    ] },
    { h: "Remember these five habits" },
    { list: [
      { lead: "Read the label", text: "before putting anything in a bin." },
      { lead: "Carry", text: "a refillable bottle, lunch box and cloth napkin." },
      { lead: "Use both sides of paper", text: "and reuse notebooks with blank pages." },
      { lead: "Never throw waste", text: "into drains, ponds, rivers or open spaces." },
      { lead: "Never burn", text: "leaves, paper or plastic in school or at home." },
    ] },
    { h: "From peel to manure" },
    { p: "Wet waste can be composted. A compost heap may contain dry twigs, green waste such as peels and grass, and brown waste such as dry leaves and torn paper. Keep it damp, turn it regularly, and never add plastic. Over time, it becomes dark, crumbly manure." },
    { h: "Classroom activity" },
    { list: [
      { lead: "Try it: Bin detective.", text: "Draw four bins and write or draw three objects for each bin. Then check whether your classroom bins are being used correctly." },
      { lead: "Think and answer.", text: "1. Why should wet food not be put in the blue bin? 2. Where should a used battery go? 3. Name one way to reduce waste at lunch." },
    ] },
  ],
};

const MODULE_2 = {
  key: "m2",
  type: "note",
  rail: "Classes 6–8",
  title: "Module 2: Classes 6–8",
  blocks: [
    { h: "Why segregation matters" },
    { p: "The Solid Waste Management Rules, 2026 require every waste generator, including homes and schools, to separate waste at the place where it is produced. The four streams are wet, dry, sanitary and special care waste. Waste should then be handed over to an authorised waste collector." },
    { h: "What happens after collection?" },
    { list: [
      { lead: "Wet waste", text: "is taken for composting or other suitable processing." },
      { lead: "Dry waste", text: "is sorted; clean paper, plastic, glass and metal can be recycled." },
      { lead: "Sanitary waste", text: "is wrapped securely and kept separate." },
      { lead: "Special care waste", text: "is sent to facilities that can handle it safely." },
    ] },
    { h: "Important responsibilities" },
    { list: [
      { lead: "Keep sanitary waste", text: "in its own wrapping or pouch before placing it in the red bin." },
      { lead: "Keep garden waste", text: "such as leaves and cuttings separate and follow the local body's directions." },
      { lead: "Do not throw, burn or bury waste", text: "in streets or open spaces." },
      { lead: "Do not put waste", text: "into drains, rivers or ponds." },
      { lead: "Do not open, break or handle", text: "damaged batteries and unsafe electronic devices." },
    ] },
    { h: "The 5Rs waste management chart for schools" },
    { list: [
      { lead: "Refuse.", text: "Say no to single-use plastics, disposable plastic pens, and unnecessary plastic wrappers or covers." },
      { lead: "Reduce.", text: "Make less waste: print on both sides and avoid unnecessary packaging." },
      { lead: "Reuse.", text: "Use an item again: repair a bag, reuse a jar or use blank notebook pages." },
      { lead: "Repurpose.", text: "Turn empty metal cans or plastic bottles into classroom planters or pencil holders, and use waste paper for scrap notes." },
      { lead: "Recycle.", text: "Send clean, separated materials to the recycling chain." },
    ] },
    { h: "E-Waste needs special care" },
    { p: "Old phones, chargers, calculators, keyboards, remotes and earphones should be collected through the school's E-Waste arrangement, if available. They must be handed to an authorised recycler. Students should not open devices or bring swollen, leaking or damaged batteries." },
    { h: "Classroom activity" },
    { list: [
      { lead: "Group task: Waste audit.", text: "For five school days, record the number of bags or kilograms of wet and dry waste. Identify the most common waste item and suggest one way to reduce it." },
      { lead: "Case study.", text: "A classroom puts food leftovers, paper and batteries in one bin. Explain three problems this can cause and propose a better system." },
    ] },
  ],
};

const MODULE_3 = {
  key: "m3",
  type: "note",
  rail: "Classes 9–12",
  title: "Module 3: Classes 9–12",
  blocks: [
    { h: "Solid waste management as a system" },
    { p: "Solid waste management begins before an item becomes waste. The preferred order is prevention, reduction, reuse, recycling, recovery and safe disposal. Landfilling should be the least preferred option for waste that can be prevented, reused, recycled or processed." },
    { h: "The Rules" },
    { list: [
      { lead: "In force.", text: "The Solid Waste Management Rules, 2026 came into force on 1 April 2026." },
      { lead: "Who they apply to.", text: "Urban and rural local bodies and domestic, institutional, commercial and other non-residential waste generators." },
      { lead: "Four streams.", text: "Waste must be separated at source into wet, dry, sanitary and special care streams." },
      { lead: "Sanitary waste", text: "must be securely wrapped before being placed in the designated stream." },
      { lead: "Prohibited.", text: "Open dumping, burning, burying and disposal into drains or water bodies." },
      { lead: "Schools and other institutions", text: "must support awareness and learning on solid waste management." },
    ] },
    { h: "Bulk waste generators" },
    { p: "An institution or entity may be treated as a bulk waste generator if it meets at least one specified threshold: a building floor area of 20,000 square metres or more, water use of 40,000 litres or more per day, or generation of 100 kilograms or more of solid waste per day. Schools, colleges and residential hostels are included in the institutional category." },
    { h: "What should a large school do?" },
    { list: [
      { lead: "Register", text: "with the local body through the prescribed online portal, where applicable." },
      { lead: "Compost", text: "or otherwise process its wet waste within the campus." },
      { lead: "Hand over", text: "dry, sanitary and special care waste only to authorised agencies." },
      { lead: "Maintain records", text: "of waste generation, segregation and processing." },
      { lead: "Train", text: "students, teachers, support staff and waste collectors in safe practices." },
    ] },
    { h: "Measure improvement" },
    { p: "A school can calculate average daily waste by dividing total waste by the number of observation days. Example: 60 kg collected over 6 days equals 10 kg per day. It can also calculate correct segregation: if bins were used correctly on 12 of 15 days, the result is 80%." },
    { h: "Student action plan" },
    { list: [
      { lead: "Map", text: "every waste point in the school and check whether the four streams are labelled." },
      { lead: "Audit", text: "conduct a baseline waste audit and set a realistic reduction target." },
      { lead: "Compost", text: "create a composting or garden-waste plan with adult supervision." },
      { lead: "E-Waste", text: "set up a safe E-Waste collection point through an authorised channel." },
      { lead: "Share", text: "results through the Eco Clubs for Mission LiFE and school assemblies." },
    ] },
    { h: "Project and reflection" },
    { list: [
      { lead: "Project: Make a school waste charter.", text: "In groups, draft five rules for students, three duties for the school administration and two indicators to review every month." },
      { lead: "Reflection.", text: "How does correct segregation protect soil, water, air and the people who collect and process waste?" },
    ] },
    { p: MATERIAL.keyMessage },
    { posters: true },
  ],
};

export const SLIDES = [MODULE_1, MODULE_2, MODULE_3];

export const QUIZ_RULES_SCHOOL = [
  { h: "10 questions", p: "The same paper for every school, presented in a shuffled order." },
  { h: "One correct option", p: "Each question has four options, A to D." },
  { h: "No time limit", p: "The paper is submitted once; review before submitting." },
  { h: "Badge", p: "9–10 Waste Warrior · 7–8 Segregation Champion · 5–6 Eco Learner." },
];

export const QUIZ_RULES_STUDENT = [
  { h: "10 random questions", p: "Drawn from a bank of 30, so every student gets a different paper." },
  { h: "One correct option", p: "Each question has four options, A to D." },
  { h: "No time limit", p: "The paper is submitted once; review before submitting." },
  { h: "Badge", p: "9–10 Waste Warrior · 7–8 Segregation Champion · 5–6 Eco Learner." },
];

export const QUIZ_RULES = QUIZ_RULES_SCHOOL;

export const BADGE_META = {
  waste_warrior: { label: "Waste Warrior", emoji: "🏆", colour: "#f2a900", blurb: "Outstanding. The school has mastered solid waste management." },
  segregation_champion: { label: "Segregation Champion", emoji: "🥈", colour: "#7c8ea3", blurb: "Excellent work. You know your bins and your rules." },
  eco_learner: { label: "Eco Learner", emoji: "🥉", colour: "#b87333", blurb: "Good start. Revisit the four streams and the duties." },
  keep_learning: { label: "Keep Learning", emoji: "🌱", colour: "#2fb457", blurb: "Re-read the campaign brief and try again when eligible." },
};
